import { Block } from '@/components/base/Block'
import InputErrorLabel from '@/components/base/Form/InputErrorLabel'
import { Col, Row } from '@/components/base/Layout'
import { Text3 } from '@/components/base/Text'
import { align, display } from '@/styles/display'
import spaceStyle from '@/styles/space'
import {
  type V1FormElementState,
  type V1FormElementUiDateSelectBox
} from 'mnvm-mypage-app-ts-if'
import { generateOptions } from './FormElementDateSelectBox'
import GenericFormElementSelectBox, {
  type SelectBoxStyle
} from './GenericFormElementSelectBox'

export interface FormElementDateRangeBoxProps {
  isEdit?: boolean
  uiIFM: V1FormElementUiDateSelectBox
  formElementState: V1FormElementState
  elemenErrorMessage?: string
  onChange: (newElementState: V1FormElementState) => void
  style?: SelectBoxStyle
}

export const FormElementDateRangeBox = ({
  isEdit = true,
  uiIFM,
  formElementState,
  elemenErrorMessage,
  onChange,
  style
}: FormElementDateRangeBoxProps): JSX.Element => {
  const selectedStartYear =
    formElementState.contentStates?.at(0)?.uint32 ?? undefined
  const selectedStartMonth =
    formElementState.contentStates?.at(1)?.uint32 ?? undefined
  const selectedEndYear =
    formElementState.contentStates?.at(2)?.uint32 ?? undefined
  const selectedEndMonth =
    formElementState.contentStates?.at(3)?.uint32 ?? undefined

  if (!isEdit) {
    // start date
    const startYearSelectedContent = uiIFM.years?.find(
      (e) => (e.state?.uint32 ?? 0) === selectedStartYear
    )
    const startYearLabel =
      startYearSelectedContent != null ? startYearSelectedContent.label : '-'

    const startMonthSelectedContent = uiIFM.months?.find(
      (e) => (e.state?.uint32 ?? 0) === selectedStartMonth
    )
    const startMonthLabel =
      startMonthSelectedContent != null ? startMonthSelectedContent.label : '-'

    // end date
    const endYearSelectedContent = uiIFM.years?.find(
      (e) => (e.state?.uint32 ?? 0) === selectedEndYear
    )
    const endYearLabel =
      endYearSelectedContent != null ? endYearSelectedContent.label : '-'

    const endMonthSelectedContent = uiIFM.months?.find(
      (e) => (e.state?.uint32 ?? 0) === selectedEndMonth
    )
    const endMonthLabel =
      endMonthSelectedContent != null ? endMonthSelectedContent.label : '-'

    return (
      <Text3 textAlign={align.LEFT} margin={{ all: spaceStyle.NONE }}>
        {startYearLabel}年/{startMonthLabel}月～{endYearLabel}年/{endMonthLabel}
        月
      </Text3>
    )
  }

  if (formElementState.type === 'FORM_ELEMENT_STATE_TYPE_UINT32_LIST') {
    const yearOptions = generateOptions(uiIFM.years ?? [])
    const monthOptions = generateOptions(uiIFM.months ?? [])

    return (
      <>
        <Row>
          <Col span={11} xs={24}>
            <Block display={display.FLEX} style={{ gap: '16px' }}>
              <Block width="50%">
                <GenericFormElementSelectBox
                  key="FormElementDateSelect_start_year"
                  options={yearOptions}
                  selectedValue={selectedStartYear}
                  hasError={elemenErrorMessage !== undefined}
                  onChange={(selectedValue) => {
                    onChange({
                      ...formElementState,
                      contentStates: [
                        {
                          label: selectedValue?.toString(),
                          uint32: Number(selectedValue)
                        },
                        {
                          label: selectedStartMonth?.toString(),
                          uint32: selectedStartMonth
                        },
                        {
                          label: selectedEndYear?.toString(),
                          uint32: selectedEndYear
                        },
                        {
                          label: selectedEndMonth?.toString(),
                          uint32: selectedEndMonth
                        }
                      ]
                    })
                  }}
                  label={{ right: '年' }}
                  style={style}
                />
              </Block>
              <Block width="50%">
                <GenericFormElementSelectBox
                  key="FormElementDateSelect_start_month"
                  options={monthOptions}
                  selectedValue={selectedStartMonth}
                  hasError={elemenErrorMessage !== undefined}
                  onChange={(selectedValue) => {
                    onChange({
                      ...formElementState,
                      contentStates: [
                        {
                          label: selectedStartYear?.toString(),
                          uint32: selectedStartYear
                        },
                        {
                          label: selectedValue?.toString(),
                          uint32: Number(selectedValue)
                        },
                        {
                          label: selectedEndYear?.toString(),
                          uint32: selectedEndYear
                        },
                        {
                          label: selectedEndMonth?.toString(),
                          uint32: selectedEndMonth
                        }
                      ]
                    })
                  }}
                  label={{ right: '月' }}
                  style={style}
                />
              </Block>
            </Block>
          </Col>
          <Col span={2} justify={align.CENTER} align={align.CENTER}>
            <span>～</span>
          </Col>
          <Col span={11} xs={24}>
            <Block display={display.FLEX} style={{ gap: '16px' }}>
              <Block width="50%">
                <GenericFormElementSelectBox
                  key="FormElementDateSelect_end_year"
                  options={yearOptions}
                  selectedValue={selectedEndYear}
                  hasError={elemenErrorMessage !== undefined}
                  onChange={(selectedValue) => {
                    onChange({
                      ...formElementState,
                      contentStates: [
                        {
                          label: selectedStartYear?.toString(),
                          uint32: selectedStartYear
                        },
                        {
                          label: selectedStartMonth?.toString(),
                          uint32: selectedStartMonth
                        },
                        {
                          label: selectedValue?.toString(),
                          uint32: Number(selectedValue)
                        },
                        {
                          label: selectedEndMonth?.toString(),
                          uint32: selectedEndMonth
                        }
                      ]
                    })
                  }}
                  label={{ right: '年' }}
                  style={style}
                />
              </Block>
              <Block width="50%">
                <GenericFormElementSelectBox
                  key="FormElementDateSelect_month"
                  options={monthOptions}
                  selectedValue={selectedEndMonth}
                  hasError={elemenErrorMessage !== undefined}
                  onChange={(selectedValue) => {
                    onChange({
                      ...formElementState,
                      contentStates: [
                        {
                          label: selectedStartYear?.toString(),
                          uint32: selectedStartYear
                        },
                        {
                          label: selectedStartMonth?.toString(),
                          uint32: selectedStartMonth
                        },
                        {
                          label: selectedEndYear?.toString(),
                          uint32: selectedEndYear
                        },
                        {
                          label: selectedValue?.toString(),
                          uint32: Number(selectedValue)
                        }
                      ]
                    })
                  }}
                  label={{ right: '月' }}
                  style={style}
                />
              </Block>
            </Block>
          </Col>
        </Row>
        {elemenErrorMessage !== undefined && (
          <InputErrorLabel message={elemenErrorMessage} />
        )}
      </>
    )
  }

  return (
    <>
      <div>not implemented</div>
    </>
  )
}
